/*
All Rights Reserved. Permission to use and distribute this software, source code and its documentation is hereby granted on the terms
and conditions set out in the License Agreement dated August 10, 2018 (‘License Agreement’) between Prashant Sharma and China Guangdong
Jingteng Technology Co., Ltd. This copyright notice shall appear in all copies and distributions to the extent permitted in the License
Agreement. This Software, source code and its documentation may not be used without the prior written consent of Prashant Sharma in any
manner whatsoever except to the extent specified in the License Agreement. Contact Prashant Sharma, B-801, Purva Panorama, Bannerghatta Road,
Bangalore – 560 076, +91 9731217354, spidersharma@gmail.com for any queries on this Software.

IN NO EVENT SHALL PRASHANT SHARMA BE LIABLE TO ANY PARTY FOR DIRECT, INDIRECT, SPECIAL, INCIDENTAL, OR CONSEQUENTIAL DAMAGES, INCLUDING LOST
PROFITS, ARISING OUT OF THE USE OF THIS SOFTWARE AND ITS DOCUMENTATION, EVEN IF PRASHANT SHARMA HAS BEEN ADVISED OF THE POSSIBILITY OF SUCH
DAMAGE.
PRASHANT SHARMA SPECIFICALLY DISCLAIMS ANY WARRANTIES, INCLUDING, BUT NOT LIMITED TO, THE IMPLIED WARRANTIES OF MERCHANTABILITY AND FITNESS
FOR A PARTICULAR PURPOSE. THE SOFTWARE AND ACCOMPANYING DOCUMENTATION, IF ANY, PROVIDED HEREUNDER IS PROVIDED "AS IS". PRASHANT SHARMA HAS
NO OBLIGATION TO PROVIDE UPDATES, ENHANCEMENTS, OR MODIFICATIONS AND WILL, HOWEVER, PROVIDE TRAINING AND MAINTENANCE TO THE EXTENT AND FOR
THE FEES AS SET OUT IN SCHEDULE B TO THE LICENSE AGREEMENT.
 */

import { Matrix4, Vector3, DoubleSide, ShaderMaterial, Color } from 'three'
import fsShader from './fs.glsl'

const NormalMapCaptureShader = {
  vertexShader: [
    'varying vec3 vNormal;',
    '#include <logdepthbuf_pars_vertex>',
    'void main() {',
    'vNormal = normal;',
    'gl_Position = projectionMatrix * modelViewMatrix * vec4( position, 1.0 );',
    '	#include <logdepthbuf_vertex>',
    '}',
  ].join('\n'),

  fragmentShader: [
    'varying vec3 vNormal;',
    '#include <logdepthbuf_pars_fragment>',
    'void main() {',
    '	#include <logdepthbuf_fragment>',
    'vec3 color = normalize(vNormal);',
    'color = color * 0.5 + 0.5;',
    'gl_FragColor = vec4( color.x, color.y, color.z, 1.0 );',
    '}',
  ].join('\n'),

  side: DoubleSide,
}

const DiamondShader = {
  defines: {
    RAY_BOUNCES: 5,
  },

  vertexShader: [
    'varying vec2 vUv;',
    'varying vec3 Normal;',
    'varying vec3 worldNormal;',
    'varying vec3 vecPos;',
    'varying vec3 viewPos;',
    '#include <logdepthbuf_pars_vertex>',
    'void main() {',
    'vUv = uv;',
    'Normal =  normal;',
    'worldNormal = (modelMatrix * vec4(normal,0.0)).xyz;',
    'vecPos = (modelMatrix * vec4(position, 1.0 )).xyz;',
    'viewPos = (modelViewMatrix * vec4(position, 1.0 )).xyz;',
    'gl_Position = projectionMatrix * modelViewMatrix * vec4( position, 1.0 );',
    '	#include <logdepthbuf_vertex>',
    '}',
  ].join('\n'),
  fragmentShader: fsShader,

  uniforms: {
    tCubeMapNormals: {
      type: 't',
      value: null,
    },
    envMap: {
      type: 't',
      value: null,
    },
    envRefractionMap: {
      type: 't',
      value: null,
    },
    sphereMap: {
      type: 't',
      value: null,
    },
    envMapIntensity: {
      type: 'f',
      value: 1.0,
    },
    maxBounces: {
      type: 'i',
      value: 1,
    },
    tanAngleSqCone: {
      type: 'f',
      value: 0.0,
    },
    coneHeight: {
      type: 'f',
      value: 0.0,
    },
    bDebugBounces: {
      type: 'i',
      value: false,
    },
    rIndexDelta: {
      type: 'f',
      value: 0.012,
    },
    n2: {
      type: 'f',
      value: 2.4,
    },
    radius: {
      type: 'f',
      value: 1.0000000036572598,
    },
    normalOffset: {
      type: 'f',
      value: 0.0,
    },
    squashFactor: {
      type: 'f',
      value: 0.9,
    },
    distanceOffset: {
      type: 'f',
      value: 0,
    },
    geometryFactor: {
      type: 'f',
      value: 0.28,
    },
    Absorbption: {
      value: new Vector3(0.0, 0.0, 0.0),
    },
    colorCorrection: {
      value: new Vector3(1.0, 1.0, 1.0),
    }, // 1/2.2
    boostFactors: {
      value: new Vector3(0.892, 0.892, 0.98595025),
    },
    centreOffset: {
      value: new Vector3(0, 0, 0),
    },
    diamondColorCorrection: {
      value: new Vector3(1, 1, 1),
    },
    // "ModelMatrix": {type: 'm4', value: new Matrix4().identity()},
    InverseModelMatrix: {
      type: 'm4',
      value: new Matrix4().identity(),
    },
    opacity: {
      type: 'f',
      value: 1.0,
    },
    i_toneMappingExposure: {
      type: 'f',
      value: 2.3,
    },
    i_toneMappingWhitePoint: {
      type: 'f',
      value: 1.0,
    },
  },
  side: DoubleSide,
}

const normalMapCaptureMaterial = new ShaderMaterial(NormalMapCaptureShader)

const diamondMaterial = new ShaderMaterial(DiamondShader)

export { normalMapCaptureMaterial, diamondMaterial }
